import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProject } from '../ProjectContext';
import { getProjectName } from '@shared/targetHelpers';
import { fetchDocuments, fetchBlobObjectUrl } from '../services/api';
import {
  buildDocumentFacets,
  filterDocuments,
  documentLocationLabel,
  hasActiveFilters,
  EMPTY_FILTERS,
} from '@shared/documentFilters';
import { Button, Loading, Empty, ErrorText, errorMessage } from '../components/ui';

function isImageDoc(doc) {
  const type = String(doc?.contentType || '').toLowerCase();
  const name = String(doc?.fileName || '').toLowerCase();
  return type.startsWith('image/') || /\.(png|jpe?g|webp|heic|heif)$/i.test(name);
}

function formatDate(value) {
  if (!value) return '';
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

// Thumbnails need the auth header, so each is fetched into an object URL and
// revoked on unmount - otherwise every scroll through the list leaks blobs.
function Thumb({ doc }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!isImageDoc(doc) || !doc.blobName) return undefined;
    let revoked = false;
    let current = '';
    fetchBlobObjectUrl(doc.blobName).then((objectUrl) => {
      if (revoked) { URL.revokeObjectURL(objectUrl); return; }
      current = objectUrl;
      setUrl(objectUrl);
    }).catch(() => {});
    return () => { revoked = true; if (current) URL.revokeObjectURL(current); };
  }, [doc.blobName]);

  if (!isImageDoc(doc)) return <div className="thumb">📄</div>;
  return url ? <img className="thumb" src={url} alt="" /> : <div className="thumb" />;
}

function FilterSelect({ label, value, options, onChange }) {
  if (!options.length) return null;
  return (
    <label className="filter-field">
      <span>{label}</span>
      <select className="filter-select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All</option>
        {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    </label>
  );
}

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { project } = useProject();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // Options come from the documents actually present, so the bar never offers
  // a value that matches nothing or hides one that exists.
  const facets = useMemo(() => buildDocumentFacets(documents), [documents]);
  const visible = useMemo(() => filterDocuments(documents, filters), [documents, filters]);

  const projectName = getProjectName(project);

  const load = useCallback(async () => {
    if (!projectName) return;
    setError('');
    try {
      setDocuments(await fetchDocuments(projectName));
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [projectName]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  return (
    <>
      <div className="row" style={{ marginBottom: 14 }}>
        <Button compact onClick={() => navigate('upload')}>Upload files</Button>
        <Button compact secondary onClick={load}>Refresh</Button>
      </div>
      <ErrorText>{error}</ErrorText>

      {!loading && documents.length > 0 && (
        <div className="filter-bar">
          {/* First, because it is what someone reaches for when they know which
              document they want. The dropdowns narrow a list you are browsing;
              this finds a file you already have in mind. */}
          <label className="filter-field" style={{ flex: '1 1 220px', minWidth: 0 }}>
            <span>Search</span>
            <input
              id="document-search"
              type="search"
              className="filter-select"
              style={{ width: '100%' }}
              value={filters.search}
              placeholder="Name, package, location or tag"
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </label>
          <FilterSelect label="Package" value={filters.package} options={facets.packages}
            onChange={(v) => setFilters((f) => ({ ...f, package: v }))} />
          <FilterSelect label="Type" value={filters.type} options={facets.types}
            onChange={(v) => setFilters((f) => ({ ...f, type: v }))} />
          <FilterSelect label="Tag" value={filters.tag} options={facets.tags}
            onChange={(v) => setFilters((f) => ({ ...f, tag: v }))} />
          {hasActiveFilters(filters) && (
            <button type="button" className="filter-clear" onClick={() => setFilters(EMPTY_FILTERS)}>
              Clear
            </button>
          )}
        </div>
      )}

      {/* Always shown, not only when filtered: "how much is here" is the first
          thing asked of a document list, and a filtered count means nothing
          without the total to read it against. */}
      {!loading && documents.length > 0 && (
        <p className="doc-count">
          {hasActiveFilters(filters)
            ? `${visible.length} of ${documents.length} document${documents.length === 1 ? '' : 's'}`
            : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
        </p>
      )}

      {loading ? <Loading text="Loading documents…" rows={4} /> : visible.map((doc, i) => (
        <button
          key={doc.blobName || i}
          type="button"
          className="list-row"
          onClick={() => navigate(`documents/${encodeURIComponent(doc.blobName)}`)}
        >
          <Thumb doc={doc} />
          <div className="list-body">
            <div className="list-title">
              {doc.fileName}
              {/* Both directions, and a document can carry both: imported
                  from Procore, analysed here, and the conclusion sent back.
                  That round trip is the thing this app is for, so it is worth
                  being able to see it on the row.

                  Each is stamped onto the document by the operation itself -
                  the import when it stores the file, the push once Procore has
                  accepted it - so an absent badge means it did not happen
                  rather than that we do not know. */}
              {doc.procore?.importedAt || doc.procore?.fileId ? (
                <span
                  className="badge-from"
                  title={[
                    doc.procore.importedAt ? `Imported from Procore ${formatDate(doc.procore.importedAt)}` : 'Imported from Procore',
                    doc.procore.folderPath ? `Procore folder: ${doc.procore.folderPath}` : '',
                  ].filter(Boolean).join('\n')}
                >
                  From Procore
                </span>
              ) : null}
              {/* Was labelled just "Procore", which was unambiguous while a
                  push was the only thing that could put a badge here and is
                  not any more. */}
              {doc.procore?.pushedAt ? (
                <span className="badge-sent" title={`Sent to Procore ${formatDate(doc.procore.pushedAt)}`}>
                  To Procore
                </span>
              ) : null}
              {/* The one state this list could not show, and the one that
                  matters most.

                  A document whose text never reached the search index looks
                  exactly like one that worked: it is in the list, it opens,
                  it has a thumbnail. The only way to discover it was to ask a
                  question you already knew the answer to and notice the app
                  say it had no record - which is the worst possible way for a
                  tool that is supposed to be checkable to fail.

                  `searchable` is decided on the server from the read-back
                  count, so the badge means the index was asked and held
                  nothing, not that a write returned an error code. Undefined
                  on older uploads that were never checked, and those get no
                  badge rather than a wrong one. */}
              {doc.indexed && doc.indexed.searchable === false ? (
                <span
                  className="badge-unsearchable"
                  title={[
                    'This document is stored but its text is not in the search index.',
                    'Questions will not find it.',
                    doc.indexed.reason ? `Reason: ${doc.indexed.reason}` : '',
                    'Re-upload it, or ask an admin to run a re-index.',
                  ].filter(Boolean).join('\n')}
                >
                  Not searchable
                </span>
              ) : null}
            </div>
            <div className="list-meta">{documentLocationLabel(doc)}</div>
            <div className="list-meta-muted">{formatDate(doc.uploadedAt)}</div>
            {doc.ai?.summary && <div className="list-ai">{doc.ai.summary}</div>}
          </div>
        </button>
      ))}
      {!loading && !documents.length && (
        <Empty icon="📄" title="No documents yet">
          Upload photos, drawings or reports and they'll appear here with their AI analysis.
        </Empty>
      )}
      {!loading && documents.length > 0 && !visible.length && (
        <Empty icon="🔍" title="Nothing matches those filters">
          Clear one of them to see more documents.
        </Empty>
      )}
    </>
  );
}
